/**
 * NewsCard — two variants matching the site's editorial grid.
 *   featured: full-width image (natural ratio) + large headline below
 *   compact:  horizontal row — thumbnail left, title+date right
 *
 * Deliberately distinct from FundraiserCard: no progress bar, no Support button.
 */

import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { hapticTap } from "@/tg/init";
import { useNavigate } from "react-router-dom";
import ImageWithFallback from "./ImageWithFallback";
import type { SectionItem } from "@/api";

interface NewsCardProps {
  item: SectionItem;
  variant: "featured" | "compact";
  divider?: boolean;
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
  } catch {
    return dateStr;
  }
}

export default function NewsCard({ item, variant, divider = true }: NewsCardProps) {
  const navigate = useNavigate();

  const formattedDate = item.date ? formatDate(item.date) : null;

  function handleTap() {
    hapticTap();
    navigate(`/detail/${item.kind}/${item.id}`);
  }

  if (variant === "featured") {
    return (
      <motion.div
        whileTap={{ scale: 0.98 }}
        onClick={handleTap}
        style={{
          borderRadius: "var(--r-card)",
          overflow: "hidden",
          background: "var(--tg-bg, #ffffff)",
          border: "1px solid var(--border, rgba(0,0,0,0.08))",
          boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
          cursor: "pointer",
          WebkitTapHighlightColor: "transparent",
          marginBottom: 12,
        }}
      >
        {/* Image — uniform 16/10 cover frame (same as fundraiser cards) */}
        <div style={{ width: "100%", aspectRatio: "16 / 9", overflow: "hidden" }}>
          <ImageWithFallback
            src={item.image}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </div>

        <div style={{ padding: "12px 14px 14px", display: "flex", flexDirection: "column" }}>
          {item.category && (<span style={{ display: "inline-block", alignSelf: "flex-start", fontSize: 13, fontWeight: 500, fontFamily: '"e-Ukraine", system-ui, sans-serif', color: "var(--ink)", background: "var(--border)", padding: "4px 10px", borderRadius: "var(--r-pill)", marginBottom: 8 }}>{item.category}</span>)}
          <h2
            className="line-clamp-3"
            style={{
              margin: 0,
              fontSize: 16,
              fontWeight: 600,
              lineHeight: 1.3,
              color: "var(--ink, var(--tg-text))",
              fontFamily: '"e-UkraineHead", system-ui, sans-serif',
            }}
          >
            {item.title}
          </h2>
          {item.subtitle && (
            <p style={{ margin: "8px 0 0", fontSize: 14, lineHeight: 1.5, color: "var(--text)", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{item.subtitle}</p>
          )}
          {formattedDate && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginTop: 10,
              }}
            >
              <span
                style={{
                  fontSize: 13,
                  color: "rgba(19,19,19,0.64)",
                  fontFamily: '"e-Ukraine", system-ui, sans-serif',
                }}
              >
                {formattedDate}
              </span>
              <ArrowUpRight size={18} color="var(--ink)" strokeWidth={2} />
            </div>
          )}
        </div>
      </motion.div>
    );
  }

  // compact — horizontal row
  return (
    <motion.div
      whileTap={{ scale: 0.98 }}
      onClick={handleTap}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: "10px 0",
        borderTop: divider ? "1px solid var(--border, rgba(0,0,0,0.07))" : "none",
        cursor: "pointer",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      {/* Thumbnail — fixed 88px square cover */}
      <div
        style={{
          width: 88,
          height: 66,
          flexShrink: 0,
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        <ImageWithFallback
          src={item.image}
          alt=""
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {item.category && (<span style={{ display: "inline-block", fontSize: 11, fontWeight: 500, fontFamily: '"e-Ukraine", system-ui, sans-serif', color: "var(--ink)", background: "var(--border)", padding: "2px 8px", borderRadius: "var(--r-pill)", marginBottom: 4 }}>{item.category}</span>)}
        <h3
          className="line-clamp-2"
          style={{
            margin: "0 0 4px",
            fontSize: 16,
            fontWeight: 600,
            lineHeight: 1.3,
            color: "var(--ink, var(--tg-text))",
            fontFamily: '"e-UkraineHead", system-ui, sans-serif',
          }}
        >
          {item.title}
        </h3>
        {item.subtitle && (
          <p style={{ margin: "0 0 4px", fontSize: 13, lineHeight: 1.45, color: "var(--text)", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{item.subtitle}</p>
        )}
        {formattedDate && (
          <p
            style={{
              margin: 0,
              fontSize: 12,
              color: "rgba(19,19,19,0.64)",
              fontFamily: '"e-Ukraine", system-ui, sans-serif',
            }}
          >
            {formattedDate}
          </p>
        )}
      </div>
    </motion.div>
  );
}
